import { useEffect, useRef, useState } from "react";
import { Check, X, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PACKAGES } from "@/data/content";
import { StatusPill } from "@/components/dashboard/kit";

const UserRow = ({ u, onSet, onPkg }) => (
  <tr data-testid={`admin-user-row-${u.email}`} className="border-t border-[var(--surface-border)] align-top">
    <td className="py-4 pr-4"><p className="font-semibold">{u.name}</p><p className="text-xs text-[var(--muted)] break-all">{u.email}</p><p className="text-xs text-[var(--muted)]">{u.phone}</p></td>
    <td className="py-4 pr-4 text-xs">
      <select data-testid={`package-select-${u.email}`} value={u.package || ""} onChange={(e) => onPkg(u, e.target.value)} className="rounded-xl px-2 py-1.5 bg-[var(--bg-2)] border border-[var(--surface-border)]">
        {!u.package && <option value="">—</option>}
        {PACKAGES.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
      </select>
      {(u.upi_id || u.account_number) && <p className="text-[var(--muted)] mt-1">UPI: {u.upi_id || "—"}{u.account_number ? ` · A/c ${u.account_number} ${u.ifsc || ""}` : ""}</p>}
    </td>
    <td className="py-4 pr-4 text-xs">{u.referrer_name ? <>{u.referrer_name}<br /><span className="text-[var(--muted)]">{u.referred_by}</span></> : "Direct"}</td>
    <td className="py-4 pr-4 text-xs"><b>{u.referral_code}</b><br /><span className="text-[var(--muted)]">{u.referral_count} joined</span></td>
    <td className="py-4 pr-4"><StatusPill status={u.status} /></td>
    <td className="py-4">
      <div className="flex gap-2">
        {u.status !== "approved" && <button data-testid={`approve-${u.email}`} onClick={() => onSet(u, "approved")} className="btn-neon !px-3 !py-1.5 text-xs"><Check size={14} /> Approve</button>}
        {u.status !== "rejected" && <button data-testid={`reject-${u.email}`} onClick={() => onSet(u, "rejected")} className="btn-ghost !px-3 !py-1.5 text-xs"><X size={14} /> Reject</button>}
        {u.status !== "pending" && <button data-testid={`reset-${u.email}`} onClick={() => onSet(u, "pending")} className="btn-ghost !px-3 !py-1.5 text-xs"><RotateCcw size={14} /></button>}
      </div>
    </td>
  </tr>
);

export default function AdminUsers({ onlyPending = false }) {
  const FILTERS = onlyPending ? ["pending"] : ["all", "approved", "pending", "rejected"];
  const [filter, setFilter] = useState(FILTERS[0]);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const ref = useRef(filter);

  const load = () => {
    const current = filter;
    api.get("/admin/users", { params: filter === "all" ? {} : { status: filter } }).then((r) => current === ref.current && setRows(r.data));
  };
  useEffect(() => { ref.current = filter; setRows([]); load(); }, [filter]); // eslint-disable-line

  const onSet = async (u, status) => {
    try { await api.patch(`/admin/users/${u.id}`, { status }); toast.success(`${u.name} → ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onPkg = async (u, pkg) => {
    try { await api.patch(`/admin/users/${u.id}`, { package: pkg }); toast.success(`${u.name}: package updated`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const filtered = rows.filter((r) => `${r.name} ${r.email} ${r.phone} ${r.referral_code}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-users-page">
      <h1 className="font-display text-3xl font-bold mb-1">{onlyPending ? "Approvals" : "All Users"}</h1>
      <p className="text-sm text-[var(--muted)] mb-6">{onlyPending ? "New IDs waiting for your approval." : "Every student ID, their package and referral."}</p>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        {!onlyPending && (
          <div className="glass rounded-full p-1 flex gap-1">
            {FILTERS.map((f) => <button key={f} data-testid={`admin-filter-${f}`} onClick={() => f !== filter && setFilter(f)} className={`rounded-full px-4 py-2 text-xs font-semibold capitalize ${filter === f ? "bg-[var(--pink)] text-[var(--on-accent)]" : "text-[var(--muted)]"}`}>{f}</button>)}
          </div>
        )}
        <label className="glass rounded-full px-4 py-2 flex items-center gap-2 text-sm ml-auto"><Search size={15} className="text-[var(--muted)]" /><input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone" className="bg-transparent outline-none w-48 sm:w-56" /></label>
      </div>
      <div className="glass rounded-2xl p-5 overflow-x-auto">
        {filtered.length === 0 ? <p data-testid="admin-empty" className="text-sm text-[var(--muted)] py-8 text-center">Nothing here yet.</p> : (
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="text-left text-xs text-[var(--muted)]"><th className="pb-3">Student</th><th>Package</th><th>Referred by</th><th>Own code</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>{filtered.map((u) => <UserRow key={u.id} u={u} onSet={onSet} onPkg={onPkg} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
